/**
 * CreateInsightsDialog — Form for launching AI-powered insights analysis
 *
 * Fields:
 * - Analysis name (text)
 * - Actor selection (ActorGroupPicker — grouped by task/audience)
 * - AI model selection (shared AvailableModelSelector)
 *
 * On submit: calls AgentApiClient.generateInsights() (fire-and-forget),
 * registers task in TaskContext for CanvasTasksPanel visibility.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Stack,
  TextField,
  Typography,
  Box,
  Alert,
  Button,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '@modules/ui-components';
import { getProviderForModel } from '@modules/ui-components';
import {
  AvailableModelSelector,
  useProjectDefaultAIModel,
} from '@modules/ai-settings';
import { AgentApiClient } from '@modules/content-agent';
import { ActorService } from '@modules/firebase-core/services/ActorService';
import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import { cleanForAI } from '@modules/builder-service/src/agent/utils/aiInputCleaners';
import { INSIGHTS_MAX_ACTORS_PER_TASK_PACKAGE } from '@modules/shared-types';
import { ActorGroupPicker } from './ActorGroupPicker';
import type { ActorGroupValidation, ActorSelection } from './ActorGroupPicker';

export interface CreateInsightsDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  /** Optional: register task for CanvasTasksPanel */
  onTaskStarted?: (taskId: string, description: string) => void;
  /** Called after successful submission */
  onSuccess?: () => void;
}

export const CreateInsightsDialog: React.FC<CreateInsightsDialogProps> = ({
  open,
  onClose,
  projectId,
  userId,
  onTaskStarted,
  onSuccess,
}) => {
  const projectDefaultModel = useProjectDefaultAIModel(projectId);
  const [name, setName] = useState('');
  const [actorSelections, setActorSelections] = useState<ActorSelection[]>([]);
  const [model, setModel] = useState<string>(projectDefaultModel.value);
  const [maxTokens, setMaxTokens] = useState(100000);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openCycle, setOpenCycle] = useState(0);
  const [modelTouched, setModelTouched] = useState(false);
  const [actorValidation, setActorValidation] = useState<ActorGroupValidation>({
    maxActorsPerTask: INSIGHTS_MAX_ACTORS_PER_TASK_PACKAGE,
    violations: [],
  });
  const wasOpenRef = useRef(false);

  const hasActorLimitViolation = actorValidation.violations.length > 0;
  const isValid = name.trim().length > 0 && actorSelections.length >= 2 && !hasActorLimitViolation;
  const canExport = actorSelections.length >= 2;

  const handleSubmit = useCallback(async () => {
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const client = new AgentApiClient();
      const response = await client.generateInsights({
        projectId,
        userId,
        actorSelections,
        analysisName: name.trim(),
        provider: getProviderForModel(model),
        model,
        maxTokens,
      });

      if (!response.success) {
        setError(response.error || 'Failed to start analysis');
        return;
      }

      // Register task for CanvasTasksPanel
      if (response.taskId) {
        onTaskStarted?.(response.taskId, `Insights: ${name.trim()}`);
      }

      onSuccess?.();
      onClose();

      // Reset form
      setName('');
      setActorSelections([]);
      setModel(projectDefaultModel.value);
      setMaxTokens(100000);
      setModelTouched(false);
    } catch (err: any) {
      setError(err.message || 'Failed to start analysis');
    } finally {
      setIsSubmitting(false);
    }
  }, [isValid, isSubmitting, projectId, userId, actorSelections, name, model, maxTokens, onTaskStarted, onSuccess, onClose, projectDefaultModel.value]);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setOpenCycle((cycle) => cycle + 1);
      setActorSelections([]);
      setActorValidation({
        maxActorsPerTask: INSIGHTS_MAX_ACTORS_PER_TASK_PACKAGE,
        violations: [],
      });
      setModel(projectDefaultModel.value);
      setModelTouched(false);
      setError(null);
    }
    wasOpenRef.current = open;
  }, [open, projectDefaultModel.value]);

  useEffect(() => {
    if (open && !modelTouched) setModel(projectDefaultModel.value);
  }, [modelTouched, open, projectDefaultModel.value]);

  // ── Export actors for insights-analyzer skill eval ─────────────────
  const handleExportForEval = useCallback(async () => {
    if (!canExport || isExporting) return;
    setIsExporting(true);
    setError(null);

    try {
      // Load full actor data with JTBD subcollections (same as agent Phase 1)
      const byAudience = new Map<string, string[]>();
      for (const sel of actorSelections) {
        if (!byAudience.has(sel.audienceId)) byAudience.set(sel.audienceId, []);
        byAudience.get(sel.audienceId)!.push(sel.actorId);
      }

      const actors: any[] = [];
      for (const [audienceId, actorIds] of byAudience) {
        const audService = new AudienceService(projectId);
        const audience = await audService.get(audienceId);
        const actService = new ActorService(projectId, audienceId);

        for (const actorId of actorIds) {
          const actor = await actService.getWithSubcollections(actorId);
          if (actor) {
            actors.push({
              ...actor,
              audienceId,
              audienceName: (audience as any)?.name ?? '',
              weight: (actor as any).weight ?? 1,
            });
          }
        }
      }

      // Clean actors using unified cleaner (same profile as insights agent)
      const cleanedActors = actors.map(a => cleanForAI('actor', a, 'insights'));

      const exportData = {
        __segmently_insights_generation_eval__: true,
        version: '1.0.0',
        exportedAt: new Date().toISOString(),
        data: {
          analysisName: name.trim() || 'Insights Analysis',
          projectId,
          cleanedActors,
        },
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `insights-input-eval-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, [canExport, isExporting, actorSelections, projectId, name]);

  const primaryAction = {
    label: isSubmitting ? 'Starting...' : 'Generate Analysis',
    onClick: handleSubmit,
    disabled: !isValid || isSubmitting,
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="insights-create-dialog"
    >
      <DialogHeader
        title="Create Insights Analysis with AI"
        onClose={onClose}
      />

      <DialogContent>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Analysis Name */}
          <TextField
            label="Analysis Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            size="small"
            placeholder="e.g., Q1 2026 Actor Analysis"
            autoFocus
            inputProps={{ 'data-testid': 'insights-analysis-name-input' }}
          />

          {/* Actor Selection */}
          <Box>
            <ActorGroupPicker
              projectId={projectId}
              value={actorSelections}
              onChange={setActorSelections}
              selectAllOnLoad
              autoSelectKey={openCycle}
              collapseActorsByDefault
              maxSelectedActorsPerTask={INSIGHTS_MAX_ACTORS_PER_TASK_PACKAGE}
              onValidationChange={setActorValidation}
            />
            {actorSelections.length > 0 && actorSelections.length < 2 && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                Select at least 2 actors for meaningful cross-actor analysis
              </Typography>
            )}
            {hasActorLimitViolation && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Reduce selection to {actorValidation.maxActorsPerTask} actors per task before generating insights.
              </Alert>
            )}
          </Box>

          {/* Model Selection + Max Tokens */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              AI Model
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <AvailableModelSelector
                projectId={projectId}
                value={model}
                onChange={(nextModel) => {
                  setModel(nextModel);
                  setModelTouched(true);
                }}
                label="Analysis model"
                size="small"
                sx={{ flex: 1, minWidth: 280 }}
                testId="insights-model-selector"
              />
              <TextField
                label="Max Tokens"
                type="number"
                size="small"
                value={maxTokens}
                onChange={(e) => setMaxTokens(Number(e.target.value) || 100000)}
                inputProps={{ min: 4000, max: 200000, step: 1000 }}
                sx={{ width: 140 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
              Defaults to the project model when configured; otherwise Gemini Flash is used. Override per run.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogFooter
        primaryAction={primaryAction}
        secondaryAction={{
          label: isExporting ? 'Exporting...' : 'Export for Eval',
          onClick: handleExportForEval,
          disabled: !canExport || isExporting || isSubmitting,
          variant: 'outlined',
          startIcon: <FileDownloadIcon />,
        }}
      />
    </BaseDialog>
  );
};
